import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Two panes and a draggable gutter. The split is persisted by `storageKey`, so
 * a workspace layout survives a reload — panel sizes are part of how someone
 * sets a review station up, not a transient.
 */
export function SplitPane({
  direction = "horizontal",
  initial = 0.5,
  min = 0.15,
  max = 0.85,
  storageKey,
  first,
  second,
}: {
  direction?: "horizontal" | "vertical";
  initial?: number;
  min?: number;
  max?: number;
  storageKey?: string;
  first: ReactNode;
  second: ReactNode;
}) {
  const stored = storageKey ? Number(localStorage.getItem(storageKey)) : NaN;
  const [fraction, setFraction] = useState(Number.isFinite(stored) && stored > 0 ? stored : initial);
  const [dragging, setDragging] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const onDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const box = ref.current?.getBoundingClientRect();
      if (!box) return;
      const value =
        direction === "horizontal"
          ? (e.clientX - box.left) / box.width
          : (e.clientY - box.top) / box.height;
      setFraction(Math.min(max, Math.max(min, value)));
    };
    const onUp = () => {
      setDragging(false);
      if (storageKey) localStorage.setItem(storageKey, String(fractionRef.current));
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, direction, min, max, storageKey]);

  const fractionRef = useRef(fraction);
  fractionRef.current = fraction;

  return (
    <div ref={ref} className={`split ${direction === "vertical" ? "vertical" : ""}`}>
      {/* shrinkable, so a pane with a min-width pushes back instead of
          overflowing the window at narrow widths */}
      <div style={{ flex: `0 1 ${fraction * 100}%`, minWidth: 0, minHeight: 0, display: "flex" }}>{first}</div>
      <div className={`gutter ${dragging ? "dragging" : ""}`} onMouseDown={onDown} />
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex" }}>{second}</div>
    </div>
  );
}
